import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { useAuth } from '@restworld/feature-authentication';
import { useNavigationItemsByUserType, useAvatarImage, useNavigationContext, cn } from '@restworld/utils-common';
import { Link } from '@restworld/ui-ds-base';

import {
  MobileOverlayNav,
  NavigationBar,
  FrontViewImpressionTextSearchbox,
  NavbarHomeTitleContent,
  HomePageNavbarTitleContent,
  LocaleSelector,
  Title,
} from '../../';
import { DesktopOverlayNav } from '../navigation-bar/navigation-bar.components/desktop-overlay-nav';
import { NavWorkerInfo } from '../navigation-bar/navigation-bar.components/nav-menu-content';

export type NavigationVariantType = 'default' | 'worker' | 'home-page' | 'home-page--with-explorer';
type NavigationBarProps = {
  authProps: {
    onStartSignup: () => void;
    onStartLogin: () => void;
    onLogout: () => void;
  };
  variant: NavigationVariantType;
  algoliaSearchBarElement?: React.ReactNode;
  showSearchBarInNavbar?: boolean; // TODO: remove this
  hasBorderBottom?: boolean;
  testId?: string;
  hideNavElement?: boolean;
  additionalContentEl?: React.ReactNode;
  workerInfo?: NavWorkerInfo;
  onShowUploadCvModal?: () => void;
  additionalNavMenuContentEl?: React.ReactNode;
  isNavOverlay: boolean;
  navOverlayToggler: React.Dispatch<React.SetStateAction<boolean>>;
};

export const DefaultNavigationBar = ({
  authProps,
  variant = 'default',
  algoliaSearchBarElement,
  hasBorderBottom = true,
  testId,
  hideNavElement,
  additionalContentEl,
  workerInfo,
  onShowUploadCvModal,
  additionalNavMenuContentEl,
  navOverlayToggler,
  isNavOverlay,
}: NavigationBarProps) => {
  const { isAuthenticated, user } = useAuth();
  const avatarProps = useAvatarImage();
  const { t } = useTranslation('common');
  const items = useNavigationItemsByUserType(user?.type, isAuthenticated);

  const defaultMobileAttributes = useMemo(
    () => ({
      isAuthenticated,
      ...avatarProps,
      ...{ isNavOverlay, navOverlayToggler },
      ...items,
      ...authProps,
    }),
    [isAuthenticated, avatarProps, isNavOverlay, navOverlayToggler, items, authProps]
  );

  const { router } = useNavigationContext();
  const locale = router.locale;
  const basePath = router.asPath;
  const mobileVersionNav = useMemo(() => {
    const background = variant === 'home-page' || variant === 'home-page--with-explorer' ? 'purple-gradient' : 'white';
    const localeComponent = (
      <LocaleSelector
        onChange={(locale) => router.push(basePath, basePath, { locale })}
        value={locale}
        background={background}
      />
    );
    if (variant === 'home-page' || variant === 'home-page--with-explorer')
      return (
        <div className="flex flex-col box-border relative w-full max-w-full bg-gradient-base rounded-b-3xl">
          <NavigationBar
            twStyle={'lg:hidden'}
            justify="between"
            hasBorderedBottom={false}
            hasRoundedBottom={false}
            background="home-mobile-nav"
            position="fixed"
            testId={testId}
            navElement={
              <MobileOverlayNav
                {...defaultMobileAttributes}
                background={background}
                localeComponent={localeComponent}
                testId={`mobile-${testId}-overlay`}
                hideNavElement={hideNavElement}
                workerInfo={workerInfo}
                onShowUploadCvModal={onShowUploadCvModal}
              />
            }
            additionalContentEl={additionalContentEl}
          />
          <FrontViewImpressionWrapper isNavOverlay={isNavOverlay}>
            <FrontViewImpressionTextSearchbox algoliaSearchBarElement={algoliaSearchBarElement}>
              {variant === 'home-page' ? <NavbarHomeTitleContent /> : <HomePageNavbarTitleContent />}
            </FrontViewImpressionTextSearchbox>
          </FrontViewImpressionWrapper>
        </div>
      );
    return (
      <NavigationBar
        twStyle={'lg:hidden'}
        justify="between"
        background={background}
        testId={testId}
        navElement={
          <MobileOverlayNav
            {...defaultMobileAttributes}
            background={background}
            localeComponent={localeComponent}
            testId={`mobile-${testId}-overlay`}
            hideNavElement={hideNavElement}
            workerInfo={workerInfo}
            onShowUploadCvModal={onShowUploadCvModal}
          >
            {algoliaSearchBarElement}
          </MobileOverlayNav>
        }
        hasBorderedBottom={hasBorderBottom}
        additionalContentEl={additionalContentEl}
      />
    );
  }, [
    variant,
    locale,
    testId,
    defaultMobileAttributes,
    hideNavElement,
    workerInfo,
    onShowUploadCvModal,
    additionalContentEl,
    isNavOverlay,
    algoliaSearchBarElement,
    hasBorderBottom,
    router,
    basePath,
  ]);

  const desktopMenuItems = useMemo(() => {
    const background = 'white';
    const localeComponent = (
      <LocaleSelector
        onChange={(locale) => router.push(basePath, basePath, { locale })}
        value={locale}
        background={background}
      />
    );

    return (
      <>
        <div className="flex items-center justify-center">{algoliaSearchBarElement}</div>
        <div className="flex items-center justify-end">
          {!isAuthenticated && (
            <Link className="mr-4" variant="neutral" href="/getting-started?utm_source=map">
              <Title textColor="neutral-700" fontWeight="medium">
                {t('publish_your_job_offer')}
              </Title>
            </Link>
          )}
          <DesktopOverlayNav
            {...defaultMobileAttributes}
            localeComponent={localeComponent}
            testId={`desktop-${testId}-overlay`}
            workerInfo={workerInfo}
            onShowUploadCvModal={onShowUploadCvModal}
          />
        </div>
      </>
    );
  }, [
    locale,
    algoliaSearchBarElement,
    // isAuthenticated,
    // t,
    defaultMobileAttributes,
    testId,
    workerInfo,
    onShowUploadCvModal,
    router,
    basePath,
  ]);

  // --- //
  const desktopVersionNav = useMemo(() => {
    return (
      <NavigationBar
        navElement={hideNavElement ? undefined : desktopMenuItems}
        justify="between"
        twStyle={'hidden lg:block'}
        testId={testId}
        additionalContentEl={additionalContentEl}
      />
    );
  }, [additionalContentEl, desktopMenuItems, hideNavElement, testId]);
  return (
    <>
      {mobileVersionNav}
      {desktopVersionNav}
      {isNavOverlay && additionalNavMenuContentEl ? additionalNavMenuContentEl : null}
    </>
  );
};

const FrontViewImpressionWrapper = ({ isNavOverlay, className, ...props }: any) => (
  <div
    className={cn('lg:hidden box-border w-full pt-[88px]', isNavOverlay ? 'md:pt-[131px] pt-[88px]' : '', className)}
    {...props}
  />
);
